import { Component, OnInit, Input, Output, EventEmitter } from "@angular/core";
import { FormGroup, AbstractControl } from "@angular/forms";
import { IComparisonCondition } from "@schema/comparison-condition";
import { IFormLeftOperand, FORM_COMPARISON_LEFT_OPERANDS, IFormLeftOperandInfo, FORM_COMPARISON_LEFT_OPERAND_OPTION } from "@schema/comparison-operand";
import { IFormComparisonOperator, FORM_COMPARISON_OPERATORS, ComparisonOperator } from "@schema/comparison-operator";
import { IconList } from "@schema/ui/icons";
import { faFontCase, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { faCaretRight } from "@fortawesome/free-solid-svg-icons";
import { sortEntityByName } from "@shared/utility/sort-entity-by-name";

@Component({
  selector: "app-comparison-condition",
  templateUrl: "./comparison-condition.component.html",
  styleUrls: ["./comparison-condition.component.scss"]
})
export class ComparisonConditionComponent implements OnInit {

  public icons: IconList = {
    caseSensitive: faFontCase,
    remove: faTrash,
    rotate: faCaretRight,
};

  /**
   * The form group
   */
  @Input()
  public formGroup: FormGroup;

  /**
   * The form group
   */
  @Input()
  public comparisonCondition: IComparisonCondition;

  /**
   * The index of the condition in the array
   * Used for the remove event.
   */
  @Input()
  public index: number;

  /**
   * The tag template used for this condition, if any
   *
   * TODO: add interface for this
   */
  @Input()
  public tagTemplate?: any;

  /**
   * If the user can remove the condition
   * (e.g. they can't either because of privileges or this is the only condition left)
   */
  @Input()
  public canRemove = false;

  /**
   * User removed the condition, send an event with the index
   */
  @Output()
  public removeConditionEvent: EventEmitter<number> = new EventEmitter<number>();

  /**
   * If the comparison condition container is collapsed or expanded
   */
  public isCollapsed = false;

  /**
   * The name of the condition (auto generated)
   */
  public get conditionTitle(): string {
    let title = `Condition #${this.index + 1}`;

    if (this.selectedLeftOperand && this.selectedOperator) {
      let leftOperand = this.selectedLeftOperand.name;
      if (this.selectedLeftOperand.type === "option") {
        leftOperand = this.leftOperands.find((operand: IFormLeftOperand) => (operand.id && operand.id === this.selectedLeftOperand.id));
        leftOperand = leftOperand ? leftOperand.name : "";
      }
      if (!leftOperand) {
        leftOperand = "";
      }
      title = `${leftOperand} ${this.selectedOperator.name.toLowerCase()}`;
      if (this.selectedOperator.hasRightOperand) {
        const rightOperand = this.rightOperand || "";
        title += ` "${rightOperand}"`;
      }
    }

    return title;
  }

  /**
   * Get the form's various controls
   */
  public get formControls(): { [key: string]: AbstractControl; } {
    return this.formGroup.controls;
  }

  /**
   * Get a list of the available left operands
   */
  public get leftOperands(): IFormLeftOperand[] {
    let operands = [];
    if (this.tagTemplate && this.tagTemplate.options) {
      operands = this.tagTemplate.options.map(option => {
        return {
          id: option.id,
          type: FORM_COMPARISON_LEFT_OPERAND_OPTION,
          name: option.name,
        };
      });
      operands.sort(sortEntityByName);
    }
    Object.entries(FORM_COMPARISON_LEFT_OPERANDS).forEach(([type, operand]: [string, IFormLeftOperandInfo]) => {
      if (type !== FORM_COMPARISON_LEFT_OPERAND_OPTION) {
        operands.push({
          id: undefined,
          name: operand.name,
          type,
        });
      }
    });
    return operands;
  }

  /**
   * Get the value of the right operand
   */
  public get rightOperand(): string {
    return this.formGroup.get("rightOperand").value;
  }

  /**
   * Get the current URI part
   */
  public get selectedLeftOperand(): any {
    if (this.formGroup) {
      return this.formGroup.get("leftOperand").value;
    }
  }

  /**
   * Get the current operator
   */
  public get selectedOperator(): IFormComparisonOperator {
    if (this.formGroup) {
      return this.formGroup.get("operator").value;
    }
  }

  /**
   * Set the current operator
   */
  public set selectedOperator(value: IFormComparisonOperator) {
    this.formGroup.patchValue({ operator: value });
  }

  /**
   * Get a list of all operators available for the selected left operand
   */
  public get operators(): IFormComparisonOperator[] {
    let operatorList = [];
    if (this.selectedLeftOperand && this.selectedLeftOperand.type) {
      const type = this.selectedLeftOperand.type;
      operatorList = FORM_COMPARISON_LEFT_OPERANDS[type].operators.reduce(
        (operators: IFormComparisonOperator[], operator: ComparisonOperator) => {
          // Grab a list of all of the valid operators + the negated version
          return operators.concat(
            FORM_COMPARISON_OPERATORS[operator],
            FORM_COMPARISON_OPERATORS[`not_${operator}`],
          );
        },
        operatorList
      );
    }
    return operatorList;
  }

  constructor() { }

  ngOnInit(): void { }

  /**
   * Indicates to the parent form that this form should be removed.
   */
  public removeCondition(): void {
    this.removeConditionEvent.emit(this.index);
  }

  /**
   * Left Operand Changed Event
   *
   * Required to set the operator since they change between left operands (e.g. we don't want "Protocol is undefined")
   */
  public leftOperandChanged(): void {
    this.formGroup.get("operator").setValue(this.operators[0]);
    this.formGroup.get("rightOperand").setValue("");
  }

  /**
   * Compare two operators to see if they are equal
   */
  public compareLeftOperands(operandA: IFormLeftOperand, operandB: IFormLeftOperand): boolean {
    if (!operandA || !operandB) {
      return !operandA && !operandB;
    }
    if (operandA.id && operandB.id) {
      return operandA.id === operandB.id;
    }
    if (operandA.type && operandB.type) {
      return operandA.type === operandB.type;
    }
    if (operandA.name && operandB.name) {
      return operandA.name === operandB.name;
    }
    return false;
  }
}
